export type DashboardTenantSlug =
  | "urban-unity"
  | "the-artillery"
  | "the-fitness-ceo"
  | "scaling-society"
  | "ascend"
  | "cr8her";

export type DashboardThemeMode = "light" | "dark" | "crimson" | "blue";

export type EmbeddedRouteKey =
  | "dashboard"
  | "leaderboard"
  | "daily-report"
  | "acquisition"
  | "closers"
  | "setters"
  | "content"
  | "payment-collection"
  | "leads"
  | "calls"
  | "financials"
  | "utm-builder"
  | "admin";

export interface DashboardTenantBranding {
  companyName: string;
  logos: {
    primary: string;
    wordmark: string;
  };
  modeLogos?: Partial<Record<DashboardThemeMode, string>>;
  invertLogoModes?: DashboardThemeMode[];
  colors: {
    primary: string;
    secondary: string;
    tertiary: string;
    accent: string;
    ring: string;
    gradient: {
      start: string;
      end: string;
      angle: number;
    };
    chart: string[];
    background: {
      light: string;
      dark: string;
    };
    card: {
      light: string;
      dark: string;
    };
  };
}

export interface DashboardTenantDefinition {
  slug: DashboardTenantSlug;
  label: string;
  companyName: string;
  dbName: string;
  collectionPrefix: string;
  /**
   * When true, this tenant's EOC data lives exclusively in its own database and
   * is NOT already included in the main infoscalers_eoc collection.
   * The OfferSelection page uses this flag to add the tenant's cash to the
   * combined "All Offers" total without double-counting other tenants.
   */
  isExternalOnly?: boolean;
  branding: DashboardTenantBranding;
  themeModes: DashboardThemeMode[];
  assetManifest: string[];
  visibleRoutes: EmbeddedRouteKey[];
}

export const EMBEDDED_ROUTE_LABELS: Record<EmbeddedRouteKey, string> = {
  dashboard: "Dashboard",
  leaderboard: "Leaderboard",
  "daily-report": "Daily Report",
  acquisition: "Acquisition",
  closers: "Closers",
  setters: "Setters",
  content: "Content",
  "payment-collection": "Payment Collection",
  leads: "Leads",
  calls: "Calls",
  financials: "P&Ls",
  "utm-builder": "UTM Builder",
  admin: "Admin",
};

const FULL_REVENUE_DASHBOARD_ROUTES: EmbeddedRouteKey[] = [
  "dashboard",
  "leaderboard",
  "daily-report",
  "acquisition",
  "closers",
  "setters",
  "content",
  "payment-collection",
  "leads",
  "calls",
  "utm-builder",
  "admin",
];

const FITNESS_CEO_ROUTES: EmbeddedRouteKey[] = [
  "dashboard",
  "leaderboard",
  "financials",
  ...FULL_REVENUE_DASHBOARD_ROUTES.filter((route) => route !== "dashboard" && route !== "leaderboard"),
];

export const DASHBOARD_TENANTS: Record<DashboardTenantSlug, DashboardTenantDefinition> = {
  "urban-unity": {
    slug: "urban-unity",
    label: "Urban Unity",
    companyName: "Urban Unity",
    dbName: "urban_unity",
    collectionPrefix: "urban_unity",
    themeModes: ["light", "dark", "crimson"],
    assetManifest: [
      "/tenants/urban-unity/logo.png",
      "/tenants/urban-unity/logo-crimson.png",
      "/tenants/urban-unity/dark-bg.jpg",
      "/tenants/urban-unity/crimson-bg.jpg",
    ],
    visibleRoutes: FULL_REVENUE_DASHBOARD_ROUTES,
    branding: {
      companyName: "Urban Unity",
      logos: {
        primary: "/tenants/urban-unity/logo.png",
        wordmark: "/tenants/urban-unity/logo.png",
      },
      modeLogos: {
        crimson: "/tenants/urban-unity/logo-crimson.png",
      },
      invertLogoModes: ["crimson"],
      colors: {
        primary: "#EF4444",
        secondary: "#F87171",
        tertiary: "#DC2626",
        accent: "#FEE2E2",
        ring: "#EF4444",
        gradient: {
          start: "#F87171",
          end: "#DC2626",
          angle: 135,
        },
        chart: ["#6366F1", "#8B5CF6", "#EC4899", "#14B8A6", "#F59E0B", "#10B981", "#3B82F6", "#EF4444"],
        background: {
          light: "#f5f5f5",
          dark: "#0a0a0a",
        },
        card: {
          light: "#ffffff",
          dark: "#171717",
        },
      },
    },
  },
  "the-artillery": {
    slug: "the-artillery",
    label: "The Artillery",
    companyName: "The Artillery",
    dbName: "artillery",
    collectionPrefix: "the_artillery",
    themeModes: ["light", "dark"],
    assetManifest: ["/tenants/the-artillery/logo.png"],
    visibleRoutes: FULL_REVENUE_DASHBOARD_ROUTES,
    branding: {
      companyName: "The Artillery",
      logos: {
        primary: "/tenants/the-artillery/logo.png",
        wordmark: "/tenants/the-artillery/logo.png",
      },
      colors: {
        primary: "#4A90D9",
        secondary: "#6AAFE8",
        tertiary: "#2D6DB5",
        accent: "#C93C3C",
        ring: "#4A90D9",
        gradient: {
          start: "#6AAFE8",
          end: "#2D6DB5",
          angle: 135,
        },
        chart: ["#4A90D9", "#C93C3C", "#E8943A", "#6AAFE8", "#2D6DB5", "#D95B5B", "#3A7BC8", "#F0B060"],
        background: {
          light: "#f0f2f5",
          dark: "#0d0d14",
        },
        card: {
          light: "#ffffff",
          dark: "#161622",
        },
      },
    },
  },
  "the-fitness-ceo": {
    slug: "the-fitness-ceo",
    label: "The Fitness CEO",
    companyName: "The Fitness CEO",
    dbName: "the_fitness_ceo",
    collectionPrefix: "the_fitness_ceo",
    themeModes: ["light", "dark", "blue"],
    assetManifest: [
      "/tenants/the-fitness-ceo/logo.png",
      "/tenants/the-fitness-ceo/logo-blue.png",
      "/tenants/the-fitness-ceo/dark-bg.jpg",
    ],
    visibleRoutes: FITNESS_CEO_ROUTES,
    branding: {
      companyName: "The Fitness CEO",
      logos: {
        primary: "/tenants/the-fitness-ceo/logo.png",
        wordmark: "/tenants/the-fitness-ceo/logo.png",
      },
      modeLogos: {
        blue: "/tenants/the-fitness-ceo/logo-blue.png",
      },
      invertLogoModes: ["dark", "blue"],
      colors: {
        primary: "#615dff",
        secondary: "#11b2f8",
        tertiary: "#504df9",
        accent: "#e8bdff",
        ring: "#09bef5",
        gradient: {
          start: "#11b2f8",
          end: "#504df9",
          angle: 135,
        },
        chart: ["#09bef5", "#615dff", "#e8bdff", "#11b2f8", "#504df9", "#a855f7", "#10b981", "#f97316"],
        background: {
          light: "#fafafa",
          dark: "#0a0a0a",
        },
        card: {
          light: "#ffffff",
          dark: "#141414",
        },
      },
    },
  },
  "scaling-society": {
    slug: "scaling-society",
    label: "Scaling Society",
    companyName: "Scaling Society",
    dbName: "scaling-society",
    collectionPrefix: "scaling_society",
    themeModes: ["light", "dark"],
    assetManifest: ["/tenants/scaling-society/logo.png"],
    visibleRoutes: FULL_REVENUE_DASHBOARD_ROUTES,
    branding: {
      companyName: "Scaling Society",
      logos: {
        primary: "/tenants/scaling-society/logo.png",
        wordmark: "/tenants/scaling-society/logo.png",
      },
      colors: {
        primary: "#6366F1",
        secondary: "#818CF8",
        tertiary: "#4F46E5",
        accent: "#E0E7FF",
        ring: "#6366F1",
        gradient: {
          start: "#818CF8",
          end: "#4F46E5",
          angle: 135,
        },
        chart: ["#6366F1", "#8B5CF6", "#06B6D4", "#10B981", "#F59E0B", "#EC4899", "#3B82F6", "#EF4444"],
        background: {
          light: "#f5f5f5",
          dark: "#0a0a0a",
        },
        card: {
          light: "#ffffff",
          dark: "#171717",
        },
      },
    },
  },
  ascend: {
    slug: "ascend",
    label: "Ascend",
    companyName: "Ascend Sales Academy",
    dbName: "ascend",
    collectionPrefix: "ascend_sales_academy",
    themeModes: ["light", "dark"],
    assetManifest: ["/tenants/ascend/logo.png"],
    visibleRoutes: FULL_REVENUE_DASHBOARD_ROUTES,
    branding: {
      companyName: "Ascend Sales Academy",
      logos: {
        primary: "/tenants/ascend/logo.png",
        wordmark: "/tenants/ascend/logo.png",
      },
      colors: {
        primary: "#C84B31",
        secondary: "#D35430",
        tertiary: "#B33A27",
        accent: "#FDEAE6",
        ring: "#C84B31",
        gradient: {
          start: "#D35430",
          end: "#A63024",
          angle: 135,
        },
        chart: ["#C84B31", "#D35430", "#E8634B", "#14B8A6", "#F59E0B", "#10B981", "#3B82F6", "#8B5CF6"],
        background: {
          light: "#f5f5f5",
          dark: "#0a0a0a",
        },
        card: {
          light: "#ffffff",
          dark: "#171717",
        },
      },
    },
  },
  cr8her: {
    slug: "cr8her",
    label: "Cr8Her",
    companyName: "Cr8Her",
    dbName: "cr8her",
    collectionPrefix: "cr8her",
    isExternalOnly: true,
    themeModes: ["light", "dark"],
    assetManifest: [],
    visibleRoutes: FULL_REVENUE_DASHBOARD_ROUTES,
    branding: {
      companyName: "Cr8Her",
      logos: {
        primary: "/tenants/cr8her/logo.png",
        wordmark: "/tenants/cr8her/logo.png",
      },
      colors: {
        primary: "#7C3AED",
        secondary: "#A855F7",
        tertiary: "#6D28D9",
        accent: "#EDE9FE",
        ring: "#7C3AED",
        gradient: {
          start: "#A855F7",
          end: "#6D28D9",
          angle: 135,
        },
        chart: ["#7C3AED", "#A855F7", "#EC4899", "#14B8A6", "#F59E0B", "#10B981", "#3B82F6", "#EF4444"],
        background: {
          light: "#f5f5f5",
          dark: "#0a0a0a",
        },
        card: {
          light: "#ffffff",
          dark: "#171717",
        },
      },
    },
  },
};

export function getDashboardTenantBySlug(slug: string | null | undefined): DashboardTenantDefinition | null {
  if (!slug) return null;
  return DASHBOARD_TENANTS[slug as DashboardTenantSlug] ?? null;
}

export function isOfferDashboardRoute(pathname: string): boolean {
  return pathname.startsWith("/offer/");
}

export function buildOfferDashboardPath(
  routeKey: EmbeddedRouteKey,
  dashboardSlug: DashboardTenantSlug
): string {
  return `/offer/${routeKey}?dashboard=${encodeURIComponent(dashboardSlug)}`;
}

export function getTenantCollectionName(tenant: DashboardTenantDefinition, baseName: string): string {
  return `${tenant.collectionPrefix}_${baseName}`;
}
